import { parseDay } from "../../../components/charts/formatters";

/**
 * Topics, freshness and the headline of Admin Dashboards panels (ASB-4377,
 * design A.5). Nothing here reads the DOM, so it is unit-tested directly.
 */

/** Where a manifest written before 2026-10-14 (no `topic`) is grouped (A.3). */
export const OTHER_TOPIC = {
	id: "other",
	title: "Other",
	description: null,
	order: Number.POSITIVE_INFINITY,
};

/**
 * The panels grouped by topic, as `[{ ...topic, panels: [...] }]`, in the order
 * GET /admin/panels returned them: the API sorts by topic order, then title,
 * then panel order (A.3), so the first panel of each topic fixes the topic's
 * place and the panels keep their order within it. The grouping never re-sorts,
 * so the server's rule has one home.
 */
export function groupByTopic(panels) {
	const groups = [];
	const byId = new Map();
	(panels ?? []).forEach((panel) => {
		const topic = panel.topic && panel.topic.id ? panel.topic : OTHER_TOPIC;
		let group = byId.get(topic.id);
		if (!group) {
			group = { ...topic, panels: [] };
			byId.set(topic.id, group);
			groups.push(group);
		}
		group.panels.push(panel);
	});
	return groups;
}

/** The topic a panel belongs to, for the breadcrumb and the picker's groups. */
export const topicOf = (panel) => (panel?.topic && panel.topic.id ? panel.topic : OTHER_TOPIC);

export const STALE_AFTER_DAYS = 2;
const DAY_MS = 86400000;

/**
 * "fresh" when the panel's latest day is within the last two days (UTC),
 * "stale" when it is older than that, "none" when nothing was computed (A.5).
 * Yesterday is the newest day a daily job can have, so a panel is fresh for
 * one missed run and stale from the second. Days before the panel's
 * `dataFrom` (A.6) do not exist: a latest day earlier than it counts as none.
 */
export function freshness(latestDay, now = new Date(), dataFrom = null) {
	const day = parseDay(latestDay);
	if (!day) return "none";
	const floor = parseDay(dataFrom);
	if (floor && day.getTime() < floor.getTime()) return "none";
	const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
	return day.getTime() < today - STALE_AFTER_DAYS * DAY_MS ? "stale" : "fresh";
}

/** The panel's latest day, or null when it lies before `dataFrom` (A.6) or is unset. */
export function effectiveLatestDay(panel) {
	const day = parseDay(panel?.latestDay);
	if (!day) return null;
	const floor = parseDay(panel?.dataFrom);
	if (floor && day.getTime() < floor.getTime()) return null;
	return String(panel.latestDay).slice(0, 10);
}

/**
 * The column a panel's `headline` names, from its dataset's manifest, or null
 * when the panel has no headline or names something its datasets do not have
 * (the validator rejects that upstream, but the page never trusts it).
 */
export function headlineColumn(panel) {
	const headline = panel?.headline;
	if (!headline || !headline.dataset || !headline.column) return null;
	const dataset = (panel.datasets ?? []).find((d) => d.id === headline.dataset);
	const column = dataset?.columns?.find((c) => c.key === headline.column);
	return column ? { dataset, column } : null;
}
